import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  DollarSign,
  Calendar,
  Users,
  BarChart3,
  PieChart,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
  Building,
  Wrench,
  RefreshCw,
  Filter
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#00D3A8', '#3B82F6', '#F59E0B', '#EF4444'];

const PredictiveDashboard = () => {
  const { token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    try {
      const buildingParam = selectedBuilding ? `building_id=${selectedBuilding}&` : '';
      
      const [overviewRes, predictionsRes, cashFlowRes, maintenanceRes, trendsRes, buildingsRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/predictive/overview${selectedBuilding ? `?building_id=${selectedBuilding}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/analytics/predictive/payment-predictions?${buildingParam}limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/analytics/predictive/cash-flow-forecast?${buildingParam}months=6`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/analytics/predictive/maintenance-forecast${selectedBuilding ? `?building_id=${selectedBuilding}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/analytics/predictive/trends?${buildingParam}period=6m`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/buildings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (predictionsRes.ok) setPredictions(await predictionsRes.json());
      if (cashFlowRes.ok) setCashFlow(await cashFlowRes.json());
      if (maintenanceRes.ok) setMaintenance(await maintenanceRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
      if (buildingsRes.ok) {
        const data = await buildingsRes.json();
        setBuildings(data.buildings || data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, selectedBuilding]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatMoney = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getRiskBadge = (risk) => {
    const styles = {
      low: 'bg-green-100 text-green-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-orange-100 text-orange-700',
      critical: 'bg-red-100 text-red-700'
    };
    const labels = {
      low: 'Faible',
      medium: 'Moyen',
      high: 'Élevé',
      critical: 'Critique'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[risk] || styles.medium}`}>
        {labels[risk] || risk}
      </span>
    );
  };

  const getHealthColor = (status) => {
    const colors = {
      excellent: 'text-green-500',
      bon: 'text-blue-500',
      attention: 'text-yellow-500',
      critique: 'text-red-500'
    };
    return colors[status] || 'text-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="predictive-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="text-[#00D3A8]" />
            Dashboard Prédictif
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Analyses et prévisions pour optimiser votre gestion
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Building Filter */}
          <select
            value={selectedBuilding}
            onChange={(e) => setSelectedBuilding(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:ring-2 focus:ring-[#00D3A8]"
          >
            <option value="">Tous les immeubles</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>{b.nom || b.name}</option>
            ))}
          </select>
          
          {/* Refresh Button */}
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
          { id: 'payments', label: 'Prédictions de paiement', icon: DollarSign },
          { id: 'cashflow', label: 'Trésorerie', icon: TrendingUp },
          { id: 'maintenance', label: 'Maintenance', icon: Wrench }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-[#00D3A8] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          {/* Health Score Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Score de santé financière</h3>
                <div className="flex items-center gap-4">
                  <span className={`text-5xl font-bold ${getHealthColor(overview.health_score?.status)}`}>
                    {overview.health_score?.score}%
                  </span>
                  <span className={`text-lg font-medium capitalize ${getHealthColor(overview.health_score?.status)}`}>
                    {overview.health_score?.status}
                  </span>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="relative w-32 h-32">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="56" fill="none" stroke="#E5E7EB" strokeWidth="12" />
                    <circle 
                      cx="64" cy="64" r="56" fill="none" 
                      stroke={overview.health_score?.color === 'green' ? '#22C55E' : 
                              overview.health_score?.color === 'blue' ? '#3B82F6' :
                              overview.health_score?.color === 'yellow' ? '#F59E0B' : '#EF4444'}
                      strokeWidth="12"
                      strokeDasharray={`${overview.health_score?.score * 3.52} 352`}
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm">Charges impayées</span>
                <DollarSign className="text-red-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-gray-800">{overview.metrics?.unpaid_charges}</p>
              <p className="text-sm text-red-500">{formatMoney(overview.metrics?.total_unpaid_amount)}</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm">En retard</span>
                <Clock className="text-orange-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-gray-800">{overview.metrics?.late_charges}</p>
              <p className="text-sm text-orange-500">{formatMoney(overview.metrics?.total_late_amount)}</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm">Taux paiement (30j)</span>
                <TrendingUp className="text-green-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-gray-800">{overview.metrics?.payment_rate_30d}%</p>
              <p className="text-sm text-gray-500">Délai moyen: {overview.metrics?.avg_payment_delay_days}j</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-sm">Maintenance prévue</span>
                <Wrench className="text-blue-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-gray-800">{overview.metrics?.maintenance_due_30d}</p>
              <p className="text-sm text-gray-500">Ce mois-ci</p>
            </div>
          </div>

          {/* Alerts */}
          {overview.alerts?.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={20} />
                Alertes prédictives
              </h3>
              <div className="space-y-3">
                {overview.alerts.map((alert, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border-l-4 ${
                      alert.severity === 'high' ? 'bg-red-50 border-red-500' :
                      alert.severity === 'medium' ? 'bg-amber-50 border-amber-500' :
                      'bg-blue-50 border-blue-500'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{alert.message}</p>
                        <p className="text-sm text-gray-600 mt-1">💡 {alert.action}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trends Chart */}
          {trends?.trends?.payments?.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Évolution des paiements</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trends.trends.payments}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => formatMoney(value)}
                    labelFormatter={(label) => `Mois: ${label}`}
                  />
                  <Bar dataKey="amount" fill="#00D3A8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {activeTab === 'payments' && predictions && (
        <div className="space-y-6">
          {/* Risk Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['critical', 'high', 'medium', 'low'].map((risk, idx) => (
              <div key={risk} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 text-sm capitalize">Risque {
                    risk === 'critical' ? 'critique' : 
                    risk === 'high' ? 'élevé' : 
                    risk === 'medium' ? 'moyen' : 'faible'
                  }</span>
                </div>
                <p className={`text-3xl font-bold ${
                  idx === 0 ? 'text-red-500' : idx === 1 ? 'text-orange-500' : idx === 2 ? 'text-yellow-500' : 'text-green-500'
                }`}>
                  {predictions.summary?.[risk] || 0}
                </p>
              </div>
            ))}
          </div>

          {/* Predictions Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Prédictions de paiement</h3>
              <p className="text-sm text-gray-500">Probabilité de paiement basée sur l'historique</p>
            </div>
            
            {predictions.predictions?.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>Aucune charge impayée à analyser</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Copropriétaire</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Immeuble</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Montant</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Retard</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Probabilité</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Risque</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {predictions.predictions.map((pred) => (
                    <tr key={pred.charge_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-800">{pred.coproprietaire_nom}</p>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">{pred.building_name}</td>
                      <td className="px-6 py-4 font-semibold">{formatMoney(pred.montant_du)}</td>
                      <td className="px-6 py-4">
                        <span className={pred.jours_retard > 30 ? 'text-red-500' : 'text-gray-600'}>
                          {pred.jours_retard}j
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                pred.probabilite_paiement >= 70 ? 'bg-green-500' :
                                pred.probabilite_paiement >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${pred.probabilite_paiement}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{pred.probabilite_paiement}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">{getRiskBadge(pred.score_risque)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'cashflow' && cashFlow && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm mb-1">Entrées prévues (6 mois)</p>
              <p className="text-2xl font-bold text-green-600">{formatMoney(cashFlow.summary?.total_income_forecast)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm mb-1">Sorties prévues (6 mois)</p>
              <p className="text-2xl font-bold text-red-600">{formatMoney(cashFlow.summary?.total_expense_forecast)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm mb-1">Solde final prévu</p>
              <p className={`text-2xl font-bold ${cashFlow.summary?.final_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatMoney(cashFlow.summary?.final_balance)}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm mb-1">Tendance</p>
              <div className="flex items-center gap-2">
                {cashFlow.summary?.trend === 'positive' ? (
                  <ArrowUpRight className="text-green-500" size={24} />
                ) : (
                  <ArrowDownRight className="text-red-500" size={24} />
                )}
                <span className={`text-lg font-semibold ${cashFlow.summary?.trend === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
                  {cashFlow.summary?.trend === 'positive' ? 'Positive' : 'Négative'}
                </span>
              </div>
            </div>
          </div>

          {/* Cash Flow Chart */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Prévision de trésorerie</h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={cashFlow.forecasts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="mois" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip 
                  formatter={(value) => formatMoney(value)}
                  labelFormatter={(label) => `Mois: ${label}`}
                />
                <Line type="monotone" dataKey="entrees_prevues" name="Entrées" stroke="#22C55E" strokeWidth={2} dot={{ fill: '#22C55E' }} />
                <Line type="monotone" dataKey="sorties_prevues" name="Sorties" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444' }} />
                <Line type="monotone" dataKey="solde_prevu" name="Solde" stroke="#3B82F6" strokeWidth={3} dot={{ fill: '#3B82F6' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Détail par mois</h3>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Mois</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Entrées</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Sorties</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Variation</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Solde</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cashFlow.forecasts?.map((f) => (
                  <tr key={f.mois} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{f.mois_label}</td>
                    <td className="px-6 py-4 text-right text-green-600">{formatMoney(f.entrees_prevues)}</td>
                    <td className="px-6 py-4 text-right text-red-600">{formatMoney(f.sorties_prevues)}</td>
                    <td className="px-6 py-4 text-right">
                      <span className={f.variation >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {f.variation >= 0 ? '+' : ''}{formatMoney(f.variation)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold">
                      <span className={f.solde_prevu >= 0 ? 'text-blue-600' : 'text-red-600'}>
                        {formatMoney(f.solde_prevu)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'maintenance' && maintenance && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm mb-1">Planifiées (90j)</p>
              <p className="text-2xl font-bold text-blue-600">{maintenance.summary?.total_scheduled}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm mb-1">En retard</p>
              <p className="text-2xl font-bold text-red-600">{maintenance.summary?.total_overdue}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm mb-1">Coût planifié</p>
              <p className="text-2xl font-bold text-gray-800">{formatMoney(maintenance.summary?.scheduled_cost)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-gray-500 text-sm mb-1">Coût retard</p>
              <p className="text-2xl font-bold text-orange-600">{formatMoney(maintenance.summary?.overdue_cost)}</p>
            </div>
          </div>

          {/* Overdue Maintenance */}
          {maintenance.overdue?.length > 0 && (
            <div className="bg-red-50 rounded-xl p-6 border border-red-100">
              <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                <AlertTriangle size={20} />
                Maintenances en retard
              </h3>
              <div className="space-y-3">
                {maintenance.overdue.map((m) => (
                  <div key={m.id} className="bg-white rounded-lg p-4 border border-red-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{m.titre}</p>
                        <p className="text-sm text-gray-500">Prévu le {m.date_prevue?.split('T')[0]}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-red-600">{m.jours_retard}j de retard</p>
                        <p className="text-sm text-gray-500">{formatMoney(m.cout_estime)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scheduled Maintenance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Maintenances planifiées (90 jours)</h3>
            </div>
            
            {maintenance.scheduled?.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>Aucune maintenance planifiée</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {maintenance.scheduled.map((m) => (
                  <div key={m.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          m.priorite === 'haute' ? 'bg-red-100 text-red-600' :
                          m.priorite === 'moyenne' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-green-100 text-green-600'
                        }`}>
                          <Wrench size={18} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{m.titre}</p>
                          <p className="text-sm text-gray-500">{m.type || 'Maintenance'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          <Calendar className="inline mr-1" size={14} />
                          {m.date_prevue?.split('T')[0]}
                        </p>
                        <p className="font-semibold">{formatMoney(m.cout_estime)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PredictiveDashboard;
